import {
  Flex,
  Box,
  Button,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Heading,
} from '@chakra-ui/react';
import {
  ProductApproval,
  productApprovalsApi,
  ProductApprovalState,
} from '@texas/api/endpoints/productApprovalsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '@texas/components/shared/Icons';
import { fadeInAnimation } from '@texas/resources/animations/animations';
import {
  MetadataList,
  MetadataListRow,
} from '@texas/components/shared/MetadataList';
import { CompositionGroupView } from '../variant/compositions/shared/group/CompositionGroupView';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { RejectReasons } from './shared/RejectReasons';
import { productApprovalEvents } from '../../shared/productApproval/events';
import { MirroringTag } from '@texas/components/shared/suppliers/MirroringTag';
import { PaMenuPopover } from './PaMenuPopover';
import { PaImage } from '@texas/components/shared/productApproval/PaImage';
import { SupplierItemCodeInput } from './shared/SupplierItemCodeInput';

export function ProductApprovalDetails({
  id,
  folderId,
  refetchAll,
}: {
  id: number;
  folderId: number;
  refetchAll: () => void;
}) {
  const {
    data: productApproval,
    refetch,
    error,
    loading,
    set,
  } = useApiResource(productApprovalsApi.get);
  const { t } = useTranslation();

  const triggerRefetch = useCallback(() => {
    refetch(id);
  }, [refetch, id]);

  useEffect(() => {
    refetch(id);
  }, [id, refetch]);

  useMemo(() => {
    return productApprovalEvents.productApprovalChanged.subscribe((event) => {
      if (event.id === id) {
        triggerRefetch();
      }
    });
  }, [triggerRefetch, id]);

  return (
    <>
      {loading && (
        <Flex w="450px" justify="center" align="center">
          <Spinner thickness="3px" color="primary.200" size="lg" />
        </Flex>
      )}

      {!loading && (
        <Box
          animation={fadeInAnimation(400)}
          borderRadius={8}
          backgroundColor="texas.bg.900"
          minW="400px"
          _light={{ backgroundColor: 'white' }}
        >
          <ErrorLabel text={error?.message} />
          {productApproval && (
            <Tabs isLazy={true}>
              <TabList>
                <Tab>{t('productApproval.details')}</Tab>
                <Tab>{t('composition.composition')}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <ProductApprovalInfo
                    refetchAll={() => {
                      refetchAll();
                      triggerRefetch();
                    }}
                    productApproval={productApproval}
                    folderId={folderId}
                    set={set}
                  />
                </TabPanel>
                <TabPanel as={Flex} justifyContent="center">
                  <CompositionGroupView
                    componentSettings={{
                      compositionArchiveRestore: 'hidden',
                      addMaterials: 'hidden',
                      materialsMode: 'read-only',
                      removeMaterials: 'hidden',
                      dimensionMode: 'read-only',
                      expander: 'hidden',
                      paStates: 'hidden',
                    }}
                    variantId={0}
                    compositionGroupId={productApproval.compositionGroupId}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </Box>
      )}
    </>
  );
}

export function ProductApprovalInfo({
  productApproval,
  folderId,
  set,
  refetchAll,
}: {
  productApproval: ProductApproval;
  folderId: number;
  set: Dispatch<SetStateAction<ProductApproval | null>>;
  refetchAll: () => void;
}) {
  const { t } = useTranslation();
  const paState = convertToEnum(ProductApprovalState, productApproval.state);

  return (
    <Flex direction="column">
      <Flex justify="center" pb={2}>
        <PaImage
          fileIdentifier={productApproval.fileIdentifier}
          fileId={productApproval.fileId}
          paId={productApproval.id}
          paNumber={productApproval.number}
          itemCode={productApproval.articleItemCode}
          articleFolderId={folderId}
          matrixNodeXValue={productApproval.matrixNodeXValue}
          matrixNodeYValue={productApproval.matrixNodeYValue}
        />
      </Flex>
      <MetadataList>
        <MetadataListRow label={t('productApproval.number')}>
          {productApproval.formatNumber}
        </MetadataListRow>
        <MetadataListRow label={t('suppliers.supplier')}>
          {productApproval.supplierName}
          {productApproval.followers &&
            productApproval.followers.length > 0 && (
              <MirroringTag ml={2} followers={productApproval.followers} />
            )}
        </MetadataListRow>
        <MetadataListRow label={t('general.branch')}>
          {productApproval.branchName}
        </MetadataListRow>
        <MetadataListRow label={t('variant.variant')}>
          {productApproval.variantName}
        </MetadataListRow>
        <MetadataListRow label={t('compositionGroup.version.supplierVersion')}>
          {t('compositionGroup.version.versionNr', {
            nr: productApproval.compositionGroupVersion,
          })}
        </MetadataListRow>
        <MetadataListRow label={t('general.option')}>
          {t('compositionGroup.option.optionNr', {
            nr: productApproval.compositionGroupBasedOnOption,
          })}
        </MetadataListRow>
        {productApproval.matrixNodeXId && (
          <MetadataListRow label={productApproval.matrixNodeXName}>
            {productApproval.matrixNodeXValue}
          </MetadataListRow>
        )}
        {productApproval.matrixNodeYId && (
          <MetadataListRow label={productApproval.matrixNodeYName}>
            {productApproval.matrixNodeYValue}
          </MetadataListRow>
        )}
        <MetadataListRow label={t('article.created')}>
          {formatDate(Locale.En, productApproval.created)}
        </MetadataListRow>
        <MetadataListRow label={t('general.state')}>
          <Flex gap={2} alignItems="center">
            <Flex direction="column">
              <Text>{productApproval.state}</Text>
              {productApproval.handledDate && (
                <Text fontSize="sm" fontWeight="bold">
                  {formatDate(Locale.En, productApproval.handledDate)}
                </Text>
              )}
            </Flex>
            <PaMenuPopover
              productApproval={productApproval}
              onChanged={() => refetchAll()}
            />
          </Flex>
        </MetadataListRow>
        {paState === ProductApprovalState.Accepted && (
          <MetadataListRow label={t('productApproval.acceptedBy')}>
            {productApproval.handledBy}
          </MetadataListRow>
        )}
        {paState === ProductApprovalState.Rejected && (
          <MetadataListRow label={t('productApproval.rejectedBy')}>
            {productApproval.handledBy}
          </MetadataListRow>
        )}
        {paState === ProductApprovalState.Rejected && (
          <MetadataListRow label={t('productApproval.reason')}>
            <RejectReasons
              reasons={productApproval.handleReason}
              justifyContent="end"
            />
          </MetadataListRow>
        )}
        {paState === ProductApprovalState.Pending &&
          productApproval.handledBy && (
            <MetadataListRow label={t('productApproval.revertedBy')}>
              {productApproval.handledBy}
            </MetadataListRow>
          )}
        {productApproval.handledNote && (
          <MetadataListRow label={t('general.note')}>
            {productApproval.handledNote}
          </MetadataListRow>
        )}
        <MetadataListRow label={t('suppliers.supplierContact')}>
          <Flex flexDir="column" align="flex-end">
            <Heading fontSize="md">
              {productApproval.supplierContactName}
            </Heading>
            {productApproval.supplierContactEmail && (
              <Text fontSize="sm" variant="sub">
                {productApproval.supplierContactEmail}
              </Text>
            )}
          </Flex>
        </MetadataListRow>
        <MetadataListRow label={t('customer.customerContact')}>
          <Flex flexDir="column" align="flex-end">
            <Heading fontSize="md">
              {productApproval.customerContactName}
            </Heading>
            {productApproval.customerContactEmail && (
              <Text fontSize="sm" variant="sub">
                {productApproval.customerContactEmail}
              </Text>
            )}
          </Flex>
        </MetadataListRow>
        <MetadataListRow label={t('productApproval.issuedBy')}>
          {productApproval.issuedBy}
        </MetadataListRow>
        <MetadataListRow label={t('productApproval.externalItemCode')}>
          <Flex gap={2} alignItems="center">
            <SupplierItemCodeInput
              productApproval={productApproval}
              onChanged={(itemCode) =>
                set({
                  ...productApproval,
                  externalSupplierItemCode: itemCode,
                })
              }
            />
          </Flex>
        </MetadataListRow>
      </MetadataList>
      <DownloadButtons productApprovalId={productApproval.id} />
    </Flex>
  );
}

function DownloadButtons({ productApprovalId }: { productApprovalId: number }) {
  const { t } = useTranslation();

  return (
    <Flex pt={4} pb={2} gap={4}>
      <Button
        variant="texas-solid"
        rightIcon={<Icons.Download />}
        as="a"
        href={productApprovalsApi.downloadSupplierProductApproval(
          productApprovalId,
        )}
        target="_blank"
        download={true}
      >
        {t('productApproval.downloadSpa')}
      </Button>
      <Menu isLazy={true}>
        <MenuButton
          as={Button}
          variant="texas-solid"
          rightIcon={<Icons.Download />}
        >
          {t('productApproval.downloadPa')}
        </MenuButton>
        <MenuList>
          <MenuItem
            as="a"
            target="_blank"
            download={true}
            href={productApprovalsApi.downloadCustomerProductApproval(
              productApprovalId,
              true,
            )}
          >
            {t('productApproval.excludeMaterialSpecifications')}
          </MenuItem>
          <MenuItem
            as="a"
            target="_blank"
            download={true}
            href={productApprovalsApi.downloadCustomerProductApproval(
              productApprovalId,
              false,
            )}
          >
            {t('productApproval.includeMaterialSpecifications')}
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}
